import { MessageCircle, Calendar } from "lucide-react";
import { RESTAURANT, ASSETS, waLink } from "@/data/restaurant";
import { useOrderCapture } from "@/components/OrderCapture";

// Picture-led hero: 4 dish photos in a bento, logo + tagline overlaid.
const PICS = [
  { src: ASSETS.jollofVeggiePlate, alt: "Jollof rice plate" },
  { src: ASSETS.suyaSkewers, alt: "Suya skewers" },
  { src: ASSETS.afangSoup, alt: "Afang soup" },
  { src: ASSETS.barbecueFishGrill, alt: "Barbecue fish on the grill" },
];

export default function Hero() {
  const { requestOrder } = useOrderCapture();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative bg-[#1a1815] text-white pt-10 sm:pt-14 pb-12 sm:pb-16 overflow-hidden"
    >
      {/* Soft background glow */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(60% 50% at 30% 0%, #C73E3A 0%, transparent 60%), radial-gradient(50% 50% at 90% 100%, #344E41 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Logo on top */}
        <div className="flex flex-col items-center text-center reveal in-view">
          <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-3xl overflow-hidden border-4 border-[#F2B441] shadow-2xl shadow-[#F2B441]/20 bg-white">
            <img
              src={ASSETS.logo}
              alt={`${RESTAURANT.name} logo`}
              data-testid="hero-logo"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center 32%", transform: "scale(1.55)" }}
            />
          </div>
          <p
            data-testid="hero-tagline"
            className="mt-5 text-[#F2B441] italic font-display text-base sm:text-lg tracking-wide"
          >
            &ldquo;{RESTAURANT.tagline}&rdquo;
          </p>
          <h1
            data-testid="hero-heading"
            className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
          >
            <span className="text-white">MERITZ</span>{" "}
            <span className="text-[#F2B441]">Restaurant</span>
          </h1>
          <p className="mt-3 text-white/75 text-sm sm:text-base">
            No 5 Figo Plaza · Okpuno · Awka
          </p>

          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={() => requestOrder(waLink())}
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eb854] text-white px-6 py-3.5 rounded-full font-semibold transition-all hover:scale-[1.03] shadow-lg shadow-black/30"
            >
              <MessageCircle size={18} /> Order on WhatsApp
            </button>
            <a
              href="#reserve"
              data-testid="hero-cta-reserve"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/30 hover:bg-white hover:text-[#2C2825] text-white px-6 py-3.5 rounded-full font-semibold transition-all"
            >
              <Calendar size={18} /> Reserve a Table
            </a>
          </div>
        </div>

        {/* Picture bento — main visual */}
        <div className="mt-10 sm:mt-14 grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 reveal in-view">
          {PICS.map((p, i) => (
            <div
              key={p.src}
              data-testid={`hero-pic-${i}`}
              className={`relative rounded-2xl overflow-hidden border border-white/10 aspect-square ${
                i === 0 ? "col-span-2 row-span-2 aspect-auto h-full min-h-[260px]" : ""
              }`}
            >
              <img
                src={p.src}
                alt={p.alt}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
